import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";

const statusColors = {
  draft: "#888",
  sent: "#4da6ff",
  paid: "#4caf50",
  overdue: "#ff6b6b",
};

const Dashboard = () => {
  const { token, user, logout } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const res = await axios.get("https://mevteck.com/invoice-api", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvoices(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = (link, id) => {
    navigator.clipboard.writeText(link);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const stats = {
    total: invoices.length,
    paid: invoices.filter((i) => i.status === "paid").length,
    unpaid: invoices.filter((i) => i.status === "sent").length,
    amount: invoices
      .filter((i) => i.status === "paid")
      .reduce((s, i) => s + i.total, 0),
  };
  const syncStatus = async (id) => {
    try {
      await axios.patch(
        `https://mevteck.com/invoice-api/${id}/status`,
        { status: "paid" },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      fetchInvoices();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Massive Designs</h2>
          <p style={styles.subtitle}>Welcome, {user?.name}</p>
        </div>
        <div style={styles.headerBtns}>
          <button
            style={styles.createBtn}
            onClick={() => navigate("/create-invoice")}
          >
            + New Invoice
          </button>
          {user?.role === 'admin' && (
            <button style={styles.createBtn} onClick={() => navigate('/users')}>
              Manage Users
            </button>
          )}
          <button
            style={styles.logoutBtn}
            onClick={() => {
              logout();
              navigate("/login");
            }}
          >
            Logout
          </button>

        </div>
      </div>

      <div style={styles.statsRow}>
        {[
          { label: 'Total Invoices', value: stats.total },
          { label: 'Paid', value: stats.paid },
          { label: 'Unpaid', value: stats.unpaid },
          ...(user?.role === 'admin' ? [{ label: 'Revenue', value: `$${stats.amount.toFixed(2)}` }] : [])
        ].map((s) => (
          <div key={s.label} style={styles.statCard}>
            <div style={styles.statVal}>{s.value}</div>
            <div style={styles.statLabel}>{s.label}</div>
          </div>
        ))}
      </div>

      <div style={styles.card}>
        <div style={styles.sectionTitle}>All Invoices</div>
        {loading ? (
          <p style={{ color: "#888" }}>Loading...</p>
        ) : invoices.length === 0 ? (
          <p style={{ color: "#888" }}>
            No invoices yet. Create your first one!
          </p>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                {[
                  "Invoice #",
                  "Client",
                  "Amount",
                  "Status",
                  "Date",
                  "PayPal Link",
                ].map((h) => (
                  <th key={h} style={styles.th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv._id} style={styles.tr}>
                  <td style={styles.td}>{inv.invoiceNumber}</td>
                  <td style={styles.td}>
                    <div style={{ color: "#fff" }}>{inv.clientName}</div>
                    <div style={{ color: "#888", fontSize: "12px" }}>
                      {inv.clientEmail}
                    </div>
                  </td>
                  <td style={styles.td}>${inv.total?.toFixed(2)}</td>
                  <td style={styles.td}>
                    <span
                      style={{
                        ...styles.badge,
                        background: statusColors[inv.status] + "22",
                        color: statusColors[inv.status],
                      }}
                    >
                      {inv.status}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span
                      style={{
                        ...styles.badge,
                        background: statusColors[inv.status] + "22",
                        color: statusColors[inv.status],
                      }}
                    >
                      {inv.status}
                    </span>
                    {inv.status === "sent" && (
                      <button
                        style={styles.syncBtn}
                        onClick={() => syncStatus(inv._id)}
                      >
                        Mark Paid
                      </button>
                    )}
                  </td>
                  <td style={styles.td}>
                    {new Date(inv.createdAt).toLocaleDateString()}
                  </td>
                  <td style={styles.td}>
                    {inv.paypalLink ? (
                      <button
                        style={styles.copyBtn}
                        onClick={() => copyLink(inv.paypalLink, inv._id)}
                      >
                        {copied === inv._id ? "✓ Copied!" : "Copy Link"}
                      </button>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    margin: "0 auto",
    padding: "2rem",
    background: "#0f0f0f",
    minHeight: "100vh",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "2rem",
  },
  title: {
    fontSize: "22px",
    fontWeight: "700",
    color: "#fff",
    marginBottom: "4px",
  },
  subtitle: { fontSize: "14px", color: "#888" },
  headerBtns: { display: "flex", gap: "10px" },
  createBtn: {
    padding: "10px 20px",
    background: "#fff",
    color: "#000",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },
  logoutBtn: {
    padding: "10px 20px",
    background: "none",
    color: "#aaa",
    border: "1px solid #333",
    borderRadius: "8px",
    fontSize: "14px",
    cursor: "pointer",
  },
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "1rem",
    marginBottom: "1.5rem",
  },
  syncBtn: {
    marginLeft: "8px",
    padding: "3px 8px",
    background: "#1a3a1a",
    color: "#4caf50",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "11px",
  },
  statCard: {
    background: "#1a1a1a",
    border: "1px solid #2a2a2a",
    borderRadius: "10px",
    padding: "1.25rem",
  },
  statVal: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#fff",
    marginBottom: "4px",
  },
  statLabel: { fontSize: "13px", color: "#888" },
  card: {
    background: "#1a1a1a",
    border: "1px solid #2a2a2a",
    borderRadius: "12px",
    padding: "1.5rem",
  },
  sectionTitle: {
    fontSize: "13px",
    color: "#888",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: "1rem",
  },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    textAlign: "left",
    fontSize: "12px",
    color: "#888",
    padding: "10px 8px",
    borderBottom: "1px solid #2a2a2a",
  },
  tr: { borderBottom: "1px solid #1f1f1f" },
  td: { padding: "12px 8px", fontSize: "14px", color: "#ccc" },
  badge: {
    padding: "3px 10px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "500",
  },
  copyBtn: {
    padding: "6px 12px",
    background: "#1f3a5f",
    color: "#4da6ff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "13px",
  },
};

export default Dashboard;
